import { Form } from "antd";
import "@ant-design/compatible/assets/index.css";
import Input from "antd/lib/input";
import InputNumber from "antd/lib/input-number";
import Select from "antd/lib/select";
import React, { FC, Fragment } from "react";
import {
	CustomField,
	CustomFieldType
} from "@/Contracts/Settings";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import { FormInstance } from "antd/lib/form";
import { NamePath, StoreValue } from "antd/lib/form/interface";
import { ProductFormValues } from "@/Contracts/Products";
import moment from "moment";
import ZDatePicker from "../ZDatePicker";

interface FormAlignment {
	labelCol: {
		span: number;
	};
	wrapperCol: {
		span: number;
	};
}

type GetFormValueProps = (name: NamePath) => StoreValue;

interface Props {
	readonly customField: CustomField;
	readonly isReadonly?: boolean;
	readonly form: FormInstance<any>;
	readonly values?: string[];
	readonly formAlignment?: FormAlignment;
	readonly customFieldList?: CustomField[];
}

const RenderCustomFieldsV4: FC<Props> = props => {
	const { customField, values, form } = props;

	const isReadonly =
		props.isReadonly || customField.is_value_editable_from_ui === false;

	const parentValueChanged = (_value: any) => {
		const childCustomField = props.customFieldList?.find(
			field => field.parent_field_info?.parent_id === customField.id
		);

		if (childCustomField) {
			form.setFieldsValue({
				custom_field_form_values: {
					[childCustomField.id]: undefined
				}
			});
		}
	};

	const getCustomFieldFormItem = (
		currentCustomField: CustomField,
		getFieldValue?: GetFormValueProps
	) => {
		switch (currentCustomField.field_type) {
			case CustomFieldType.TextField: {
				return (
					<Input
						data-testid="text-custom-field"
						disabled={isReadonly}
						placeholder={`Please enter ${currentCustomField.display_name}`}
					/>
				);
			}
			case CustomFieldType.NumberField: {
				const inputOnChange = (
					event: React.KeyboardEvent<HTMLInputElement>
				) => {
					if (event.key === "." || event.key === "e") {
						event.preventDefault();
					}
				};
				return (
					<InputNumber
						data-testid="number-custom-field"
						disabled={isReadonly}
						min={0}
						step={1}
						type="number"
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="full-width"
						onKeyDown={inputOnChange}
					/>
				);
			}
			case CustomFieldType.DecimalField:
			case CustomFieldType.AmountField: {
				const inputOnChange = (
					event: React.KeyboardEvent<HTMLInputElement>
				) => {
					if (event.key === "e") {
						event.preventDefault();
					}
				};
				return (
					<InputNumber
						data-testid="decimal-custom-field"
						disabled={isReadonly}
						min={0}
						step={0.01}
						type="number"
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="full-width"
						onKeyDown={inputOnChange}
					/>
				);
			}
			case CustomFieldType.Dropdown: {
				const parentCustomFieldId =
					currentCustomField.parent_field_info?.parent_id;

				if (!!parentCustomFieldId && getFieldValue) {
					const parentCustomField = props.customFieldList?.find(
						field => field.id === parentCustomFieldId
					);

					if (!parentCustomField) {
						throw new Error(
							`No parent custom field for ${currentCustomField.display_name} found !!`
						);
					}

					const parentCustomFieldValue: string = getFieldValue([
						"custom_field_form_values",
						parentCustomField.id
					]);

					const allowedValues =
						currentCustomField.parent_field_info?.allowed_values?.[
							parentCustomFieldValue
						];
					return (
						<Select
							data-testid={`child.${customField.id}`}
							disabled={isReadonly}
							placeholder={`Please select ${currentCustomField.display_name}`}
						>
							{allowedValues?.map(
								(allowedDropdownValue, index) => {
									return (
										<Select.Option
											key={index}
											value={allowedDropdownValue}
										>
											{allowedDropdownValue}
										</Select.Option>
									);
								}
							)}
						</Select>
					);
				}

				return (
					<Select
						data-testid={`dropdown.${customField.id}`}
						disabled={isReadonly}
						placeholder={`Please select ${currentCustomField.display_name}`}
						onChange={parentValueChanged}
					>
						{currentCustomField.allowed_dropdown_values?.length >
							0 &&
							currentCustomField.allowed_dropdown_values.map(
								(allowedDropdownValue, index) => {
									return (
										<Select.Option
											key={index}
											value={allowedDropdownValue}
										>
											{allowedDropdownValue}
										</Select.Option>
									);
								}
							)}
					</Select>
				);
			}
			case CustomFieldType.DateField: {
				return (
					<ZDatePicker
						data-testid={`date.${customField.id}`}
						disabled={isReadonly}
						placeholder={`Please enter ${currentCustomField.display_name}`}
						className="date-picker full-width"
					/>
				);
			}
			case CustomFieldType.DropdownFieldWithKeyValue: {
				return (
					<Select
						placeholder={`Please select ${customField.display_name}`}
					>
						{customField.allowed_dropdown_key_values?.map(field => (
							<Select.Option key={field.key} value={field.key}>
								{field.value}
							</Select.Option>
						))}
					</Select>
				);
			}
			default: {
				return <Fragment />;
			}
		}
	};

	const isDateCustomField =
		customField.field_type === CustomFieldType.DateField;

	const initialValue = values
		? isDateCustomField && values[0]
			? moment(values[0])
			: values[0]
		: undefined;

	return !!customField.parent_field_info?.parent_id ? (
		<Form.Item
			noStyle
			shouldUpdate={(
				prevValue: ProductFormValues,
				nextValue: ProductFormValues
			) =>
				!!customField.parent_field_info?.parent_id &&
				prevValue.custom_field_form_values?.[
					customField.parent_field_info?.parent_id
				] !==
					nextValue.custom_field_form_values?.[
						customField.parent_field_info?.parent_id
					]
			}
		>
			{({ getFieldValue }) => (
				<Form.Item
					labelCol={props.formAlignment?.labelCol ?? { span: 4 }}
					wrapperCol={props.formAlignment?.wrapperCol ?? { span: 20 }}
					label={`${capitalizeFirstLetter(customField.display_name)}`}
					labelAlign="left"
					hasFeedback={true}
					name={["custom_field_form_values", customField.id]}
					initialValue={initialValue}
					rules={[
						{
							required:
								customField.mandatory_field_details?.for_ui ??
								false,
							message: `Please enter ${customField.display_name}`
						}
					]}
				>
					{getCustomFieldFormItem(customField, getFieldValue)}
				</Form.Item>
			)}
		</Form.Item>
	) : (
		<Form.Item
			labelCol={props.formAlignment?.labelCol ?? { span: 4 }}
			wrapperCol={props.formAlignment?.wrapperCol ?? { span: 20 }}
			label={`${capitalizeFirstLetter(customField.display_name)}`}
			labelAlign="left"
			hasFeedback={true}
			name={["custom_field_form_values", customField.id]}
			initialValue={initialValue}
			rules={[
				{
					required:
						customField.mandatory_field_details?.for_ui ?? false,
					message: `Please enter ${customField.display_name}`
				}
			]}
		>
			{getCustomFieldFormItem(customField)}
		</Form.Item>
	);
};

export default RenderCustomFieldsV4;
